import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useDebouncedValue, useRequestGate } from '../../hooks/useLatestRequest';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// const MOCK_INVOICES = [
//   { id: 'INV-2026-0201', client: 'Gruppenpraxis Bern AG',   policy: 'POL-IJM-2026-0092', amount: 45000, status: 'paid',    dueDate: '2026-02-01', product: 'Daily Sickness',         paidDate: '2026-01-28' },
//   { id: 'INV-2026-0202', client: 'Dr. Martin Weber',        policy: 'POL-RC-2026-0042',  amount: 8400,  status: 'sent',    dueDate: '2026-03-01', product: 'Professional Liability', paidDate: null },
//   { id: 'INV-2026-0203', client: 'Centre Médical Lausanne', policy: 'POL-IJM-2026-0189', amount: 22000, status: 'overdue', dueDate: '2026-02-15', product: 'Daily Sickness',         paidDate: null },
//   { id: 'INV-2026-0204', client: 'Dr. Sophie Müller',       policy: 'POL-PJ-2026-0331',  amount: 2800,  status: 'sent',    dueDate: '2026-03-10', product: 'Legal Protection',       paidDate: null },
//   { id: 'INV-2026-0205', client: 'Dr. Jean-Luc Favre',      policy: 'POL-RC-2026-0015',  amount: 12500, status: 'paid',    dueDate: '2026-01-15', product: 'Professional Liability', paidDate: '2026-01-12' },
//   { id: 'INV-2026-0206', client: 'Dr. Elena Rossi',         policy: 'POL-RC-2026-0008',  amount: 6800,  status: 'draft',   dueDate: '2026-03-15', product: 'Professional Liability', paidDate: null },
//   { id: 'INV-2026-0207', client: 'Praxis am See AG',        policy: 'POL-IJM-2026-0201', amount: 31000, status: 'sent',    dueDate: '2026-03-01', product: 'Daily Sickness',         paidDate: null },
//   { id: 'INV-2026-0208', client: 'Dr. Thomas Brunner',      policy: 'POL-PJ-2025-0287',  amount: 5200,  status: 'cancelled', dueDate: '2026-01-01', product: 'Legal Protection',     paidDate: null },
// ];

// const MOCK_AGING = [
//   { label: '0–30 days',  count: 3, amount: 61800, color: '#10b981' },
//   { label: '31–60 days', count: 1, amount: 22000, color: '#f59e0b' },
//   { label: '61–90 days', count: 0, amount: 0,     color: '#ea580c' },
//   { label: '90+ days',   count: 0, amount: 0,     color: '#ef4444' },
// ];

const toDisplay = inv => ({
  id: inv.invoiceNumber || inv._id,
  client: inv.client,
  policy: inv.policyNumber || inv.policy || '—',
  amount: inv.amount,
  status: inv.status,
  dueDate: inv.dueDate,
  paidDate: inv.paidDate,
  product: inv.product || '—',
  _id: inv._id,
});

const AGING_COLORS = ['#10b981', '#f59e0b', '#ea580c', '#ef4444'];

const useInvoicingFunctions = () => {
  useLocale();
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [aging, setAging] = useState([]);
  const [search, setSearch] = useState('');
  // Query on the settled value, and let only the newest request's reply
  // reach the screen -- see hooks/useLatestRequest.js.
  const debouncedSearch = useDebouncedValue(search, 300);
  const beginRequest = useRequestGate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('invoices');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [paying, setPaying] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 20;

  const fetchInvoices = useCallback(async () => {
    const req = beginRequest();
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      // D-27: 'outstanding' is a virtual filter — expand to the concrete
      // statuses the backend understands so ?status=draft,sent,overdue
      // returns everything that isn't paid or cancelled. If the backend
      // doesn't accept comma-separated, the client-side `filtered` below
      // still narrows the current-page rows correctly.
      if (statusFilter === 'outstanding') {
        params.set('status', 'draft,sent,overdue');
      } else if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (debouncedSearch) params.set('search', debouncedSearch);
      const resp = await axios.get(`${BASE_URL}/invoices?${params}`, { withCredentials: true , signal: req.signal });
      if (!req.isCurrent()) return;
      if (resp.data?.data) {
        setInvoices(resp.data.data.map(toDisplay));
        setTotal(resp.data.total || resp.data.data.length);
        if (resp.data.aging) {
          setAging(resp.data.aging.map((a, i) => ({ ...a, color: AGING_COLORS[i] })));
        }
      }
    } catch {
      /* empty state on error */
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, beginRequest]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleCloseNew = useCallback(() => setShowNew(false), []);
  const handleCreated = useCallback(() => {
    fetchInvoices();
    setShowNew(false);
  }, [fetchInvoices]);

  const handleRecordPayment = async inv => {
    setPaying(inv._id);
    try {
      await axios.patch(`${BASE_URL}/invoices/${inv._id}/pay`, {}, { withCredentials: true });
      fetchInvoices();
    } catch {
      setInvoices(prev => prev.map(i => (i._id === inv._id ? { ...i, status: 'paid', paidDate: new Date().toISOString() } : i)));
    } finally {
      setPaying(null);
    }
  };

  const handleMarkAsSent = async inv => {
    try {
      await axios.patch(`${BASE_URL}/invoices/${inv._id}`, { status: 'sent' }, { withCredentials: true });
      setInvoices(prev => prev.map(i => (i._id === inv._id ? { ...i, status: 'sent' } : i)));
    } catch {
      /* silent — UI already optimistic */
    }
  };

  const filtered = invoices.filter(inv => {
    const q = search.toLowerCase();
    // D-27: expand 'outstanding' virtual status to the concrete set
    // client-side too, so filtering works even if the backend ignored
    // the comma-separated status list and returned all rows.
    if (statusFilter === 'outstanding') {
      if (inv.status === 'paid' || inv.status === 'cancelled') return false;
    } else if (statusFilter !== 'all' && inv.status !== statusFilter) {
      return false;
    }
    if (q && !inv.id.toLowerCase().includes(q) && !inv.client.toLowerCase().includes(q) && !(inv.policy || '').toLowerCase().includes(q)) return false;
    return true;
  });

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const totalOutstanding = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').reduce((s, i) => s + i.amount, 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);

  const TABS = [
    { id: 'invoices', label: t('screens.invoicing.tabInvoices') },
    { id: 'aging', label: t('screens.invoicing.tabAging') },
  ];

  return {
    invoices,
    aging,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    activeTab,
    setActiveTab,
    page,
    setPage,
    total,
    paying,
    showNew,
    setShowNew,
    loading,
    handleCloseNew,
    handleCreated,
    handleRecordPayment,
    handleMarkAsSent,
    filtered,
    paginated,
    totalPages,
    totalOutstanding,
    totalOverdue,
    totalPaid,
    TABS,
  };
};

export default useInvoicingFunctions;
